import React, { useMemo, useState, useEffect } from "react";
import { Row, Col, Typography, Spin, Alert } from "antd";
import { useLocation } from "react-router-dom";
import ListingCard from "./components/ListingCard";
import FiltersBar from "./components/FiltersBar";
import SortAndPagination from "./components/SortAndPagination";
import InsightsDrawer from "../RecommendedListings/components/InsightsDrawer/InsightsDrawer";
import usePrivateListings from "./hooks/usePrivateListings";
import useMakes from "./hooks/useMakes";
import { fetchMarketplaceTaskStatus } from "./services/api";
import "./PrivateListings.css";

const { Text } = Typography;

// Utility to get earliest fetched_at date with time
function getEarliestFetchedAt(listings) {
  if (!Array.isArray(listings) || listings.length === 0) return null;
  const dates = listings
    .map(l => l.fetched_at)
    .filter(Boolean)
    .map(d => new Date(d));
  if (dates.length === 0) return null;
  const earliest = new Date(Math.min(...dates));
  const dateStr = `${earliest.getMonth() + 1}/${earliest.getDate()}/${earliest.getFullYear()}`;
  const timeStr = earliest.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  return `${dateStr} ${timeStr}`;
}

export default function PrivateListings() {
  const location = useLocation();
  const [filters, setFilters] = useState({ make: undefined });
  const [applied, setApplied] = useState({ make: undefined });
  
  // State for marketplace task status
  const [isTaskComplete, setIsTaskComplete] = useState(true); // Default to true to avoid flicker
  const [isCheckingTask, setIsCheckingTask] = useState(true);

  // Force re-render when route changes
  useEffect(() => {
    // Component will re-render when navigating to private listings
  }, [location.pathname]);

  // Check marketplace task status on mount
  useEffect(() => {
    const checkTaskStatus = async () => {
      try {
        setIsCheckingTask(true);
        const response = await fetchMarketplaceTaskStatus();
        const isComplete = response?.is_complete ?? response?.data?.is_complete ?? true;
        setIsTaskComplete(isComplete);
      } catch (error) {
        console.error('Failed to fetch marketplace task status:', error);
        // On error, assume complete to show content
        setIsTaskComplete(true);
      } finally {
        setIsCheckingTask(false);
      }
    };
    
    checkTaskStatus();
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drawerWidth, setDrawerWidth] = useState(500);

  // Calculate drawer width (slightly smaller than Recommended Listings width)
  useEffect(() => {
    const calculateWidth = () => {
      if (window.innerWidth <= 768) {
        setDrawerWidth('100%');
      } else if (window.innerWidth > 1400) {
        setDrawerWidth(850); // Larger width for big screens
      } else {
        setDrawerWidth(750); // Larger width for medium screens
      }
    };
    
    calculateWidth();
    window.addEventListener('resize', calculateWidth);
    return () => window.removeEventListener('resize', calculateWidth);
  }, []);

  // Compose API params
  const apiParams = useMemo(() => ({
    make: applied.make,
    sort_by: "listing_date",
    sort_order: "desc",
    limit: 100,
    offset: 0,
  }), [applied]);

  const { listings, count, loading, error, fetched_at } = usePrivateListings(apiParams);
  const { makes, loading: makesLoading } = useMakes();

  // Drawer handlers
  const handleOpenDrawer = (item) => {
    setSelected(item);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
  };

  // Format the fetched_at date from API response with date and time
  // Convert UTC timestamp to user's local timezone
  const lastUpdated = useMemo(() => {
    if (fetched_at) {
      // Ensure the date string is treated as UTC (add 'Z' if not present)
      const utcString = fetched_at.endsWith('Z') ? fetched_at : `${fetched_at}Z`;
      const utcDate = new Date(utcString);
      
      // Format in user's local timezone
      const dateStr = utcDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      const timeStr = utcDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true
      });
      return `${dateStr} ${timeStr}`;
    }
    return null;
  }, [fetched_at]);

  // Normalize Private Listings data structure to match Recommended Listings drawer format
  const normalizeListingForDrawer = useMemo(() => {
    if (!selected) return null;
    
    return {
      // Basic vehicle info
      year: selected.year,
      make: selected.make || selected.vehicle_make,
      model: selected.model,
      trim: selected.trim,
      version: selected.version,
      
      // Location
      city: selected.city,
      state: selected.state,
      zipcode: selected.zipcode,
      
      // Pricing - map from Private Listings format to Recommended Listings format
      askingPrice: selected.asking_price || selected.price || 0,
      retailPrice: selected.retail || selected.retail_price || null,
      marketAvg: selected.market_avg || null,
      estProfit: selected.profit || selected.est_profit || null,
      
      // Other fields
      dom: selected.dom || selected.days_on_market || null,
      unitsSold30d: selected.units_sold_30d || selected.sold_count || null,
      activeListings: selected.active_count || null,
      grade: selected.grade || null,
      listingUrl: selected.vdp_url || selected.url || null,
      demand: selected.demand || "High",
      ai: selected.ai || {},
      
      // Date fields - map listing_date to multiple possible fields
      listingDate: selected.listing_date,
      listedAt: selected.listing_date,
      postedAt: selected.listing_date,
      
      // IDs for API calls
      id: selected.id,
      listingId: selected.id,
      vin: selected.vin,
      
      // Additional fields that might be needed
      miles: selected.miles || selected.mileage,
      mileage: selected.mileage || selected.miles,
      location: selected.location || selected.city_state,
      title: selected.title,
      
      // Keep original data for any fallback needs
      ...selected
    };
  }, [selected]);



  return (
    <div className="private-listings-page">
      <div className="description-section">
        <Text style={{ color: "#b6c1d4", fontSize: 16 }}>
          Displays only vehicles listed by genuine private sellers, excluding dealers, flippers, and resellers. Filters out cars with salvage titles, flood damage, or other major mechanical issues. Focuses on clean-title, retail-ready listings that offer high sourcing potential for dealerships.
          <span style={{ color: "#8ea0c6", fontSize: 13, display: "block", marginTop: 8 }}>
            <b>Disclaimer:</b> Although we strive to show only high-quality, filtered listings, some unsuitable vehicles may occasionally slip through.
          </span>
        </Text>
      </div>

      <div className="last-updated">
        <div style={{ margin: "16px 0 20px", color: "#a5b4fc", fontSize: 14, fontWeight: 600, letterSpacing: 0.2 }}>
          Listings Last Updated: {lastUpdated || "N/A"}
          {loading && (
            <span style={{ marginLeft: 12, color: "#10b981", fontSize: 12 }}>
              🔄 Refreshing data...
            </span>
          )}
        </div>
      </div>

      <FiltersBar
        makes={makes}
        values={filters}
        onChange={setFilters}
        onApply={() => setApplied(filters)}
        onReset={() => {
          const resetFilters = { make: undefined };
          setFilters(resetFilters);
          setApplied(resetFilters);
          setCurrentPage(1);
        }}
        loading={loading}
      />

      <SortAndPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        total={count} // <-- now uses total_count from API
      />

      {isCheckingTask || !isTaskComplete ? (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '60px 20px',
          textAlign: 'center'
        }}>
          <Spin size="large" />
          <div style={{ 
            marginTop: 16, 
            color: '#8ea0c6', 
            fontSize: 16, 
            fontWeight: 500 
          }}>
            Still fetching recommended listings for your Market
          </div>
        </div>
      ) : loading ? (
        <div className="loading-skeleton">
          <Spin size="large" />
        </div>
      ) : error ? (
        <div className="error-state">
          <Alert type="error" message="Failed to load listings" description={error?.message} />
        </div>
      ) : (
        <div className="listings-section">
          <div className="listings-grid">
            {listings?.map((item) => (
              <div key={item.id} className="listing-card-wrapper">
                <ListingCard item={item} onViewInsights={() => handleOpenDrawer(item)} />
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Insights Drawer */}
      <InsightsDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        listing={normalizeListingForDrawer}
        onWatchlist={(l) => {/* Add to watchlist functionality */}}
        drawerWidth={drawerWidth}
      />
    </div>
  );
}
